import {
	Container,
	Heading,
	Paragraph,
	Skeleton,
	Text,
	Tooltip,
} from '@audio-network/componentlibrary'
import { Info, Sparkle } from '@audio-network/componentlibrary/icons'
import React, { useEffect, useState } from 'react'
import { SORT_OPTIONS, isMostRecentSort } from '@/constants/search'
import {
	useInfiniteHits,
	useInstantSearch,
	useSearchBox,
	useStats,
	useToggleRefinement,
} from 'react-instantsearch'

import { Button } from '@audio-network/componentlibrary'
import NoResultsPromptSearch from './NoResultsPromptSearch/NoResultsPromptSearch'
import TrackList from '@/components/shared/TrackList'
import { TrackRowSkeleton } from '@/components/shared/TrackRow'
import { clientGetToggleFromCookie } from '@/utils/cookies/getToggleFromCookie'
import { createInfiniteHitsSessionStorageCache } from 'instantsearch.js/es/lib/infiniteHitsCache'
import { mapTracksFromAlgolia } from './TrackResults.utils'
import styles from './TrackResults.module.css'
import { useAISearchRouter } from '@/pages/track/searchkeyword/index.hooks'
import useAiSearchStore from '@/stores/aiSearchStore'
import { usePersistSearchScrollPosition } from '@/pages/track/searchkeyword/index.hooks'
import { useRefinements } from '../CurrentRefinements/CurrentRefinements.hooks'
import { useSortBy } from 'react-instantsearch'
import { useTranslation } from 'next-i18next'

const sessionStorageCache = createInfiniteHitsSessionStorageCache()

const TrackResults = ({
	playlistTracks,
	trackIndex,
	user,
}: {
	playlistTracks: Track[]
	trackIndex: string
	user: User
}): JSX.Element => {
	const { status, uiState } = useInstantSearch()
	const { nbHits, page, hitsPerPage } = useStats()
	const { isLastPage, items, showMore } = useInfiniteHits({
		cache: sessionStorageCache,
	})

	const { containerRef } = usePersistSearchScrollPosition()

	const [isLoadingMore, setIsLoadingMore] = useState(false)
	const [isEmptySearch, setIsEmptySearch] = useState(true)
	const [hasSearched, setHasSearched] = useState(false)
	const [showNoResultsPromptSearch, setShowNoResultsPromptSearch] =
		useState(false)
	const { t } = useTranslation(['search'])

	const { value: instrumentalUiToggle } = useToggleRefinement({
		attribute: 'Instrumental',
	})
	const { refinements } = useRefinements()
	const { query } = useSearchBox()

	const {
		aiSearchPending,
		searchByPrompt,
		aiSearchResults,
		aiSearchHasFailed,
	} = useAiSearchStore()

	const isInitialLoading =
		!isLoadingMore && (status === 'stalled' || status === 'loading')

	const aiResultsNoSearchHits =
		!aiSearchHasFailed && aiSearchResults && nbHits === 0

	const areAiResultsOutOfSync =
		aiSearchResults &&
		items.length > 0 &&
		!items.every((item) => aiSearchResults.some((result) => item.Id === result))

	const { currentRefinement } = useSortBy(SORT_OPTIONS)

	const showSkeleton =
		isInitialLoading || aiSearchPending || areAiResultsOutOfSync

	const handleShowMore = (): void => {
		setIsLoadingMore(true)

		showMore()
	}

	const tooltipContent = (
		<div className={styles.tooltipContent}>
			<div className={styles.tooltipTitle}>
				<Sparkle className={styles.tooltipIcon} color="white" />
				<Text color="white" bold>
					{t('prompt-search-tooltip-title')}
				</Text>
			</div>
			<Text color="white" variant="bodyS">
				{t('prompt-search-tooltip-description')}
			</Text>
		</div>
	)

	useEffect(() => {
		if (status === 'idle') {
			setIsLoadingMore(false)
		}
	}, [status])

	useEffect(() => {
		// We set isEmptySearch to true when the instrumental search toggle is not selected and we have no other refinements
		setIsEmptySearch(
			!instrumentalUiToggle.isRefined && refinements.length === 0
		)
	}, [refinements, instrumentalUiToggle])

	useEffect(() => {
		// We set hasSearched to true when the search is loading and user has not searched yet
		if (status === 'loading' && !hasSearched) {
			setHasSearched(true)
		}
	}, [hasSearched, status])

	useEffect(() => {
		if (
			user &&
			refinements.length === 1 &&
			refinements[0]?.isSearchByPrompt === null
		) {
			setShowNoResultsPromptSearch(true)
		} else {
			setShowNoResultsPromptSearch(false)
		}
	}, [refinements, user])

	useAISearchRouter(!!user, uiState, trackIndex)
	const shouldUseAiSearchRanking =
		aiSearchResults && !isMostRecentSort(currentRefinement)

	const mappedTrackResults = items?.length
		? mapTracksFromAlgolia(
				/**
				 * We slice the array by page size because the default behaviour of InstantSearch
				 * means that loading more (increasing page number) and hitting the back button
				 * does not remove further results
				 */
				items?.slice(0, (page + 1) * hitsPerPage),
				shouldUseAiSearchRanking ? aiSearchResults : null
		  )
		: []

	return (
		<Container maxWidth="large" ref={containerRef}>
			{showSkeleton && (
				<>
					<Heading level={2} size="S">
						<Skeleton data-testid="skeleton" width={192} />
					</Heading>
					{Array(16)
						.fill(0)
						?.map((_, index) => {
							return (
								<TrackRowSkeleton
									data-testid="skeleton"
									key={`skeleton-${index}`}
								/>
							)
						})}
				</>
			)}
			{(nbHits === 0 || aiSearchHasFailed || aiResultsNoSearchHits) &&
				!showSkeleton &&
				hasSearched && (
					<>
						<Paragraph align="center">{t('no-results')}</Paragraph>
						{clientGetToggleFromCookie('NoResultsPromptSearchLink') &&
							showNoResultsPromptSearch && (
								<NoResultsPromptSearch query={query} />
							)}
					</>
				)}
			{!showSkeleton && nbHits !== 0 && !aiSearchHasFailed && (
				<>
					<div className={styles.trackCountWrapper}>
						{searchByPrompt && (
							<div className={styles.aiIcon} data-testid="prompt-search-icon">
								<Sparkle color="white" />
							</div>
						)}
						<Heading level={2} size="XS" uppercase>
							{isEmptySearch
								? t('latest-tracks')
								: `${nbHits.toLocaleString()} ${t('tracks')}`}
						</Heading>
						{searchByPrompt && (
							<Tooltip
								id="prompt-search-tooltip"
								className={styles.promptSearchTooltip}
								content={tooltipContent}
								color="electricBlue"
							>
								<Info className={styles.promptSearchInfoIcon} />
							</Tooltip>
						)}
					</div>
					<TrackList
						tracks={isEmptySearch ? playlistTracks : mappedTrackResults}
						source="Search"
					></TrackList>
					{isLoadingMore && (
						<>
							{Array(16)
								.fill(0)
								?.map((_, index) => {
									return (
										<TrackRowSkeleton
											data-testid="skeleton"
											key={`skeleton-${index}`}
										/>
									)
								})}
						</>
					)}
					{!isEmptySearch && !isLastPage && !isLoadingMore && (
						<Button className={styles.showMore} onClick={handleShowMore}>
							{t('show-more-tracks')}
						</Button>
					)}
				</>
			)}
		</Container>
	)
}

export default TrackResults
